import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPageContent, subscribeToPage } from '../firebase/firebase-config';
import '../styles/rich-text.css';

function Academics() {
  const [content, setContent] = useState({
    title: 'Academics',
    subtitle: 'Excellence in Education',
    description: 'MIS International School offers a comprehensive academic program following the CBSE curriculum. Our academic approach focuses on holistic development, critical thinking, and practical learning experiences.',
    sliderImages: [],
    totalStudents: '5000+',
    totalTeachers: '200+',
    passRate: '98%',
    yearsOfExcellence: '30+',
    showHighlights: 'yes',
    highlightsTitle: 'Academic Highlights',
    academicHighlights: [],
    showPrograms: 'yes',
    programsTitle: 'Academic Programs',
    academicPrograms: [],
    showResults: 'yes',
    resultsTitle: 'Board Exam Results',
    resultsDescription: '',
    results: [],
    academicLinks: [
      { name: 'Curriculum', path: '/curriculum', icon: '📖', description: '', features: '' },
      { name: 'Syllabus', path: '/syllabus', icon: '📋', description: '', features: '' },
      { name: 'Question Papers', path: '/question-papers', icon: '📝', description: '', features: '' },
      { name: 'Holiday Homework', path: '/holiday-homework', icon: '🎒', description: '', features: '' },
      { name: 'Time Table', path: '/time-table', icon: '⏰', description: '', features: '' },
      { name: 'Academic Calendar', path: '/calendar', icon: '📅', description: '', features: '' },
      { name: 'Teachers & Staff', path: '/teachers', icon: '👨‍🏫', description: '', features: '' },
      { name: 'Mandatory Disclosure', path: '/mandatory-disclosure', icon: '⚖️', description: '', features: '' }
    ]
  });
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    primaryColor: '#1a3a6b',
    secondaryColor: '#f59e0b'
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [academicsData, settingsData] = await Promise.all([
          getPageContent('academics'),
          import('../firebase/firebase-config').then(module => module.getSettings())
        ]);
        setContent(prev => ({ ...prev, ...academicsData }));
        if (settingsData) setSettings(settingsData);
      } catch (error) {
        console.error('Error loading academics page:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();

    const unsubscribe = subscribeToPage('academics', (data) => {
      if (data) setContent(prev => ({ ...prev, ...data }));
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Academics...</p>
      </div>
    );
  }

  const primaryColor = settings.primaryColor || '#1a3a6b';
  const secondaryColor = settings.secondaryColor || '#f59e0b';

  // Get stats from CMS
  const stats = [
    { number: content.totalStudents || '5000+', label: 'Students' },
    { number: content.totalTeachers || '200+', label: 'Teachers' },
    { number: content.passRate || '98%', label: 'Pass Rate' },
    { number: content.yearsOfExcellence || '30+', label: 'Years of Excellence' }
  ];

  // Get academic links from CMS with custom features
  const academicLinks = content.academicLinks || [];

  // Get academic highlights
  const academicHighlights = content.academicHighlights || [];

  // Get academic programs
  const academicPrograms = content.academicPrograms || [];

  // Get results
  const results = content.results || [];

  // Default features for links if not provided
  const getDefaultFeatures = (linkName) => {
    const featuresMap = {
      'Curriculum': ['CBSE Affiliation', 'Subject Details', 'Teaching Methods', 'Assessment Pattern'],
      'Syllabus': ['Class-wise Syllabus', 'Term-wise Breakup', 'Downloadable PDFs', 'Latest Updates'],
      'Question Papers': ['Previous Year Papers', 'Sample Papers', 'Annual/Half-Yearly', 'Subject-wise'],
      'Holiday Homework': ['Summer Vacation', 'Winter Break', 'Diwali Holidays', 'Project Work'],
      'Time Table': ['Class-wise Schedule', 'Period Timings', 'Subject Allocation', 'Download/Print'],
      'Academic Calendar': ['Session Calendar', 'Exam Dates', 'Holiday List', 'Event Schedule'],
      'Teachers & Staff': ['Faculty Directory', 'Qualifications', 'Subject Expertise', 'Experience'],
      'Mandatory Disclosure': ['School Information', 'Staff Details', 'Student Strength', 'Infrastructure']
    };
    return featuresMap[linkName] || ['Information Available', 'Resources', 'Updates', 'Downloads'];
  };

  // Get default icon for link if not provided
  const getDefaultIcon = (linkName) => {
    const iconMap = {
      'Curriculum': '📖',
      'Syllabus': '📋',
      'Question Papers': '📝',
      'Holiday Homework': '🎒',
      'Time Table': '⏰',
      'Academic Calendar': '📅',
      'Teachers & Staff': '👨‍🏫',
      'Mandatory Disclosure': '⚖️'
    };
    return iconMap[linkName] || '📚';
  };

  // Get default color for link
  const getDefaultColor = (linkName) => {
    const colorMap = {
      'Curriculum': '#1a3a6b',
      'Syllabus': '#2d5aa0',
      'Question Papers': '#f59e0b',
      'Holiday Homework': '#10b981',
      'Time Table': '#8b5cf6',
      'Academic Calendar': '#ef4444',
      'Teachers & Staff': '#ec4899',
      'Mandatory Disclosure': '#64748b'
    };
    return colorMap[linkName] || '#1a3a6b';
  };

  // Parse features string to array
  const parseFeatures = (featuresStr) => {
    if (!featuresStr) return [];
    return featuresStr.split(',').map(f => f.trim()).filter(f => f);
  };

  return (
    <div className="academics-page">
      {/* Page Banner */}
      
    
         
    
    

      <div className="academics-container">
        <div className="container">
          {/* Description Section - Using dangerouslySetInnerHTML */}
          <div className="description-section">
            <h2 style={{ color: primaryColor }}>Welcome to {content.title}</h2>
            <div className="divider" style={{ background: secondaryColor }}></div>
            <div 
              className="rich-text-content"
              dangerouslySetInnerHTML={{ __html: content.description || '' }}
            />
          </div>

          {/* Stats Section - Quick Highlights */}
          <div className="stats-highlights">
            {stats.map((stat, index) => (
              <div key={index} className="stat-box">
                <div className="stat-number" style={{ color: primaryColor }}>{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Academic Highlights Section - Using dangerouslySetInnerHTML */}
          {content.showHighlights !== 'no' && academicHighlights.length > 0 && (
            <div className="highlights-section">
              <div className="sections-title">
                <h2 style={{ color: primaryColor }}>{content.highlightsTitle || 'Academic Highlights'}</h2>
                <div className="divider" style={{ background: secondaryColor }}></div>
              </div>
              <div className="highlights-grid">
                {academicHighlights.map((highlight, index) => (
                  <div key={index} className="highlight-card">
                    <div className="highlight-icon" style={{ background: secondaryColor }}>
                      {highlight.icon || '📚'}
                    </div>
                    <h3 style={{ color: primaryColor }}>{highlight.title}</h3>
                    <div 
                      className="rich-text-content"
                      dangerouslySetInnerHTML={{ __html: highlight.description || '' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Academic Programs Section - Using dangerouslySetInnerHTML */}
          {content.showPrograms !== 'no' && academicPrograms.length > 0 && (
            <div className="programs-section">
              <div className="sections-title">
                <h2 style={{ color: primaryColor }}>{content.programsTitle || 'Academic Programs'}</h2>
                <div className="divider" style={{ background: secondaryColor }}></div>
              </div>
              <div className="programs-grid">
                {academicPrograms.map((program, index) => (
                  <div key={index} className="program-card">
                    <div className="program-header" style={{ borderBottomColor: secondaryColor }}>
                      <h3>{program.name}</h3>
                      {program.grades && <span className="program-grades">{program.grades}</span>}
                    </div>
                    <div className="program-content">
                      <div 
                        className="rich-text-content"
                        dangerouslySetInnerHTML={{ __html: program.description || '' }}
                      />
                      {program.features && (
                        <div className="program-features">
                          {program.features.split(',').map((feature, idx) => (
                            <span key={idx} className="feature-tag">{feature.trim()}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Academic Resources Section */}
          <div className="sections-title">
            <h2 style={{ color: primaryColor }}>Academic Resources</h2>
            <div className="divider" style={{ background: secondaryColor }}></div>
            <p>Explore all academic resources and information</p>
          </div>

          <div className="academic-grid">
            {academicLinks.map((link, index) => {
              const features = link.features ? parseFeatures(link.features) : getDefaultFeatures(link.name);
              const icon = link.icon || getDefaultIcon(link.name);
              const color = getDefaultColor(link.name);
              
              return (
                <Link to={link.path} key={index} className="academic-card">
                  <div className="card-icon" style={{ backgroundColor: color }}>
                    {icon}
                  </div>
                  <div className="card-content">
                    <h3>{link.name}</h3>
                    {link.description ? (
                      <div 
                        className="rich-text-content"
                        dangerouslySetInnerHTML={{ __html: link.description }}
                      />
                    ) : (
                      <p>Access {link.name.toLowerCase()} resources, documents, and information</p>
                    )}
                    <div className="card-features">
                      {features.map((feature, idx) => (
                        <span key={idx} className="feature-tag">{feature}</span>
                      ))}
                    </div>
                    <div className="card-arrow">
                      <span>Explore →</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Results Section - Using dangerouslySetInnerHTML */}
          {content.showResults !== 'no' && (results.length > 0 || content.resultsDescription) && (
            <div className="results-section">
              <div className="sections-title">
                <h2 style={{ color: primaryColor }}>{content.resultsTitle || 'Board Exam Results'}</h2>
                <div className="divider" style={{ background: secondaryColor }}></div>
              </div>
              
              {content.resultsDescription && (
                <div 
                  className="results-description rich-text-content"
                  dangerouslySetInnerHTML={{ __html: content.resultsDescription }}
                />
              )}
              
              {results.length > 0 && (
                <div className="results-grid">
                  {results.map((result, index) => (
                    <div key={index} className="result-card">
                      <div className="result-year" style={{ background: secondaryColor }}>{result.year}</div>
                      <div className="result-stats">
                        <div className="stat-item">
                          <span className="stat-label">Pass Percentage</span>
                          <span className="stat-value" style={{ color: primaryColor }}>{result.percentage}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Total Students</span>
                          <span className="stat-value">{result.totalStudents}</span>
                        </div>
                      </div>
                      {result.remarks && (
                        <div 
                          className="result-remarks rich-text-content"
                          dangerouslySetInnerHTML={{ __html: result.remarks }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Important Links Section */}
          <div className="important-links">
            <h3 style={{ color: primaryColor }}>Important Information</h3>
            <div className="links-grid">
              <Link to="/examination-schedule" className="info-link">
                <span className="link-icon">📝</span>
                <span>Examination Schedule</span>
              </Link>
           
            
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .academics-page {
          background: #f8fafc;
          min-height: 100vh;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e2e8f0;
          border-top-color: ${primaryColor};
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Page Banner */
        .page-banner {
          position: relative;
          height: 350px;
          overflow: hidden;
        }

        .default-banner {
          background: linear-gradient(135deg, ${primaryColor}, ${secondaryColor});
        }

        .banner-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .banner-content h1 {
          font-size: 48px;
          color: white;
          margin-bottom: 15px;
          animation: fadeInUp 0.8s ease;
        }

        .banner-content p {
          font-size: 20px;
          color: rgba(255, 255, 255, 0.9);
          animation: fadeInUp 0.8s ease 0.2s both;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 60px 24px;
        }

        /* Description Section */
        .description-section {
          text-align: center;
          margin-bottom: 50px;
        }

        .description-section h2 {
          font-size: 36px;
          margin-bottom: 15px;
        }

        .divider {
          width: 80px;
          height: 4px;
          margin: 0 auto 25px;
          border-radius: 2px;
        }

        /* Stats Highlights */
        .stats-highlights {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 25px;
          margin-bottom: 60px;
        }

        .stat-box {
          background: white;
          padding: 30px 20px;
          text-align: center;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s, box-shadow 0.3s;
        }

        .stat-box:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
        }

        .stat-number {
          font-size: 36px;
          font-weight: 800;
          margin-bottom: 10px;
        }

        .stat-label {
          font-size: 14px;
          color: #64748b;
          font-weight: 500;
        }

        /* Highlights Section */
        .highlights-section {
          margin-bottom: 60px;
        }

        .highlights-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 30px;
        }

        .highlight-card {
          background: white;
          padding: 30px;
          border-radius: 20px;
          text-align: center;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s;
        }

        .highlight-card:hover {
          transform: translateY(-5px);
        }

        .highlight-icon {
          width: 70px;
          height: 70px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 35px;
          border-radius: 50%;
          margin: 0 auto 20px;
          color: white;
        }

        .highlight-card h3 {
          font-size: 20px;
          margin-bottom: 12px;
        }

        /* Programs Section */
        .programs-section {
          margin-bottom: 60px;
        }

        .programs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 30px;
        }

        .program-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s;
        }

        .program-card:hover {
          transform: translateY(-5px);
        }

        .program-header {
          padding: 20px;
          border-bottom: 3px solid;
          text-align: center;
        }

        .program-header h3 {
          font-size: 22px;
          color: #1e293b;
          margin-bottom: 5px;
        }

        .program-grades {
          font-size: 14px;
          color: #64748b;
        }

        .program-content {
          padding: 20px;
        }

        .program-features {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 15px;
        }

        /* Sections Title */
        .sections-title {
          text-align: center;
          margin-bottom: 40px;
        }

        .sections-title h2 {
          font-size: 32px;
          margin-bottom: 15px;
        }

        .sections-title p {
          color: #64748b;
          margin-top: 15px;
        }

        /* Academic Grid */
        .academic-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 30px;
          margin-bottom: 60px;
        }

        .academic-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          text-decoration: none;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          display: flex;
          flex-direction: column;
        }

        .academic-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
        }

        .card-icon {
          width: 80px;
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 40px;
          margin: 25px auto 0;
          border-radius: 20px;
          color: white;
          transition: all 0.3s;
        }

        .academic-card:hover .card-icon {
          transform: scale(1.1);
        }

        .card-content {
          padding: 20px 25px 25px;
        }

        .card-content h3 {
          font-size: 22px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 12px;
          text-align: center;
        }

        .card-content p {
          font-size: 14px;
          color: #64748b;
          line-height: 1.6;
          text-align: center;
          margin-bottom: 15px;
        }

        .card-features {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          justify-content: center;
          margin-bottom: 20px;
        }

        .feature-tag {
          font-size: 11px;
          padding: 4px 10px;
          background: #f1f5f9;
          color: #475569;
          border-radius: 20px;
          transition: all 0.3s;
        }

        .academic-card:hover .feature-tag {
          background: #e2e8f0;
        }

        .card-arrow {
          text-align: center;
          padding-top: 10px;
          border-top: 1px solid #e2e8f0;
        }

        .card-arrow span {
          font-size: 14px;
          font-weight: 600;
          color: ${primaryColor};
          transition: all 0.3s;
        }

        .academic-card:hover .card-arrow span {
          color: ${secondaryColor};
          letter-spacing: 1px;
        }

        /* Results Section */
        .results-section {
          margin-bottom: 60px;
        }

        .results-description {
          text-align: center;
          max-width: 800px;
          margin: 0 auto 30px;
        }

        .results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 25px;
        }

        .result-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          transition: transform 0.3s;
        }

        .result-card:hover {
          transform: translateY(-3px);
        }

        .result-year {
          padding: 12px;
          text-align: center;
          color: white;
          font-size: 20px;
          font-weight: 700;
        }

        .result-stats {
          padding: 20px;
        }

        .stat-item {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid #e2e8f0;
        }

        .stat-item:last-child {
          border-bottom: none;
        }

        .stat-value {
          font-weight: 700;
          font-size: 16px;
        }

        .result-remarks {
          padding: 0 20px 20px;
          font-size: 13px;
          color: #64748b;
        }

        /* Important Links */
        .important-links {
          background: white;
          border-radius: 20px;
          padding: 35px;
          margin-top: 20px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
        }

        .important-links h3 {
          font-size: 24px;
          text-align: center;
          margin-bottom: 25px;
        }

        .links-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 15px;
        }

        .info-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 20px;
          background: #f8fafc;
          border-radius: 12px;
          text-decoration: none;
          color: #334155;
          font-weight: 500;
          transition: all 0.3s;
        }

        .info-link:hover {
          background: ${primaryColor};
          color: white;
          transform: translateX(5px);
        }

        .info-link:hover .link-icon {
          color: white;
        }

        .link-icon {
          font-size: 20px;
          color: ${secondaryColor};
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .container {
            padding: 50px 20px;
          }
          .academic-grid {
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          }
        }

        @media (max-width: 768px) {
          .banner-content h1 {
            font-size: 32px;
          }
          .banner-content p {
            font-size: 16px;
          }
          .container {
            padding: 40px 20px;
          }
          .description-section h2 {
            font-size: 28px;
          }
          .stats-highlights {
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }
          .stat-box {
            padding: 20px 15px;
          }
          .stat-number {
            font-size: 28px;
          }
          .academic-grid {
            grid-template-columns: 1fr;
          }
          .programs-grid {
            grid-template-columns: 1fr;
          }
          .highlights-grid {
            grid-template-columns: 1fr;
          }
          .important-links {
            padding: 25px;
          }
          .links-grid {
            grid-template-columns: 1fr;
          }
          .sections-title h2 {
            font-size: 26px;
          }
        }

        @media (max-width: 480px) {
          .stats-highlights {
            grid-template-columns: 1fr;
          }
          .banner-content h1 {
            font-size: 28px;
          }
          .description-section h2 {
            font-size: 24px;
          }
          .results-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

export default Academics;